export type TextareaAutosizeOptions = {
  autosize?: boolean;
  minRows?: number;
  maxRows?: number;
};

const parseCssPixelValue = (value: string | null | undefined): number | null => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (!normalized || normalized === 'none' || normalized === 'auto' || normalized === 'normal') {
    return null;
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const escapeForRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getElementClassName = (element: HTMLElement): string => {
  if (typeof element.className === 'string') return element.className;
  return element.getAttribute('class') ?? '';
};

const parseTailwindArbitraryPixelUtility = (
  className: string,
  utilityPrefix: string,
): number | null => {
  const match = className.match(
    new RegExp(`(?:^|\\s)(?:[\\w-]+:)*${escapeForRegExp(utilityPrefix)}\\[(\\d+(?:\\.\\d+)?)px\\](?=\\s|$)`),
  );
  if (!match) return null;

  const parsed = Number.parseFloat(match[1] ?? '');
  return Number.isFinite(parsed) ? parsed : null;
};

const collectPositivePixels = (values: Array<number | null>): number[] => values.filter((value): value is number => (
  typeof value === 'number' && Number.isFinite(value) && value > 0
));

export const autoResizeTextarea = (
  element: HTMLTextAreaElement | null,
  options: TextareaAutosizeOptions = {},
): void => {
  const { autosize, minRows, maxRows } = options;
  if (!element || !autosize) return;

  const computed = window.getComputedStyle(element);
  const className = getElementClassName(element);
  const lineHeight = Number.parseFloat(computed.lineHeight) || Number.parseFloat(computed.fontSize) * 1.4 || 20;
  const paddingTop = Number.parseFloat(computed.paddingTop) || 0;
  const paddingBottom = Number.parseFloat(computed.paddingBottom) || 0;
  const borderTop = Number.parseFloat(computed.borderTopWidth) || 0;
  const borderBottom = Number.parseFloat(computed.borderBottomWidth) || 0;
  const chromeHeight = paddingTop + paddingBottom + borderTop + borderBottom;

  const rowMinimumHeight = Math.max(1, minRows ?? 1) * lineHeight + chromeHeight;
  const rowMaximumHeight = maxRows
    ? Math.max(rowMinimumHeight, maxRows * lineHeight + chromeHeight)
    : Number.POSITIVE_INFINITY;

  const inlineMinimumHeight = parseCssPixelValue(element.style.minHeight);
  const inlineMaximumHeight = parseCssPixelValue(element.style.maxHeight);
  const cssMinimumHeight = parseCssPixelValue(computed.minHeight);
  const cssMaximumHeight = parseCssPixelValue(computed.maxHeight);
  const classMinimumHeight = parseTailwindArbitraryPixelUtility(className, 'min-h-');
  const classMaximumHeight = parseTailwindArbitraryPixelUtility(className, 'max-h-');

  const minimumHeight = Math.max(
    ...collectPositivePixels([rowMinimumHeight, inlineMinimumHeight, cssMinimumHeight, classMinimumHeight]),
  );

  const maximumConstraintCandidates = collectPositivePixels([
    Number.isFinite(rowMaximumHeight) ? rowMaximumHeight : null,
    inlineMaximumHeight,
    cssMaximumHeight,
    classMaximumHeight,
  ]);

  const maximumHeight = maximumConstraintCandidates.length > 0
    ? Math.max(minimumHeight, Math.min(...maximumConstraintCandidates))
    : Number.POSITIVE_INFINITY;

  element.style.height = 'auto';
  const nextHeight = Math.max(minimumHeight, Math.min(element.scrollHeight, maximumHeight));
  element.style.height = `${nextHeight}px`;
  element.style.overflowY = element.scrollHeight > maximumHeight ? 'auto' : 'hidden';
};
